import React, { useState, useRef, useEffect } from 'react'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import MicIcon from '@mui/icons-material/Mic'
import MicNoneIcon from '@mui/icons-material/MicNone'
import {
    Tooltip,
    Box,
    TextareaAutosize,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Switch,
    Button,
    Fade,
    IconButton,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material'

import usePageInfo from '../../hooks/usePageInfo'
import { useZustand } from '../../hooks/useZustand'
import useVoiceDictation, {
    isVoiceDictationSupported,
} from '../../hooks/useVoiceDictation'
import { transcribeVoiceAudio } from '../../hooks/useApi'
import { useSnackbar } from '../Snackbar'
import VoiceSourceMenu from '../VoiceInput/VoiceSourceMenu'
import { isCallSource } from '../../utils/voiceSource'

import Tags from './Tags'
import { wrapSelectionInBrackets } from '../../utils/bracketSelection'

const selectorShowSnackbar = state => state.showSnackbar

// Room along the textarea's bottom edge for the voice controls row
// (auto-save switch, LIVE badge, spinner, mic), so text never runs under it.
const VOICE_CONTROLS_GUTTER = 36
// Past this the field scrolls instead of growing (and pushing the form's
// buttons out of the panel).
const TEXTAREA_MAX_ROWS = 10


const StatementTextarea = ({
    initialText,
    open,
    onSave,
    onCancel,
    onEnd,
    voiceEnabled = false,
}) => {
    const [text, setText] = useState(initialText || '')
    // Tentative words of the statement being dictated right now; shown after
    // `text` in the field and replaced by the final transcript at the pause.
    const [livePreview, setLivePreview] = useState('')
    const showSnackbar = useSnackbar(selectorShowSnackbar)

    const { label } = usePageInfo()
    const comparisonMode = useZustand.getState().comparisonMode

    const storageKey = label ? `extractEntities_${label}` : null

    const getDefaultExtractEntities = () => {
        const stored = storageKey && localStorage.getItem(storageKey)
        if (stored !== null) return stored === 'true'
        // window prefix: userFactory is an EJS-page global absent on
        // standalone React pages like /import/* — a bare reference throws
        return (
            window.userFactory?.getContextOptions?.(
                'partOfSpeechToProcess'
            ) === 'HASHTAGS_ONLY'
        )
    }

    const [extractEntities, setExtractEntities] = useState(
        getDefaultExtractEntities
    )
    const [tags, setTags] = useState([])
    const [showEntityWarning, setShowEntityWarning] = useState(false)

    const textareaRef = useRef(null) // Create a ref for the textarea
    const mirrorRef = useRef(null) // Create a ref for the caret-tracking mirror

    useEffect(() => {
        if (label) setExtractEntities(getDefaultExtractEntities())
    }, [label])

    const handleExtractEntitiesChange = checked => {
        setExtractEntities(checked)
        if (storageKey) localStorage.setItem(storageKey, String(checked))
    }

    useEffect(() => {
        // Focus the textarea when the component is opened
        if (textareaRef.current) {
            setTimeout(() => {
                textareaRef.current?.focus()
            }, 0)
        }
    }, [open])

    useEffect(() => {
        if (window.userFactory?.getGraphOption('chat')) {
            setTags(['you'])
        }
    }, [])

    const doSave = async () => {
        await onSave({ text, tags, extractEntities })
        setText('')
        setTags([])
    }

    const handleSave = async () => {
        if (extractEntities && (text.includes('[[') || text.includes(']]'))) {
            setShowEntityWarning(true)
            return
        }
        await doSave()
    }

    // --- Voice dictation start ---
    // A segment's final text arrives only after the pause that ends it, so
    // the async handler reads the form's latest values through a ref rather
    // than the closure it was created in.
    // Auto-save on (always, when the form opens): every transcribed segment
    // is saved as it arrives. Off: segments pile up in the field (speaker
    // categories become tag chips) until the user clicks Save or switches
    // auto-save back on. Deliberately not remembered across visits — a
    // silently-off switch looks like voice input that stopped saving.
    const [autoSave, setAutoSave] = useState(true)

    const latestFormRef = useRef({})
    latestFormRef.current = { text, tags, extractEntities, onSave, autoSave }

    const appendPending = items => {
        setText(current => {
            let next = current.trimEnd()
            for (const item of items) {
                // Speaker turns go on their own lines; plain dictation flows.
                const separator = !next ? '' : item.categories?.length ? '\n' : ' '
                next = `${next}${separator}${item.text}`
            }
            return next
        })
        const categories = items.flatMap(item => item.categories || [])
        if (categories.length > 0) {
            setTags(current => [...new Set([...current, ...categories])])
        }
    }

    const voice = useVoiceDictation({
        transcribe: (blob, filename, meta) =>
            transcribeVoiceAudio(
                blob,
                filename,
                useZustand.getState().currentContext,
                meta
            ),
        onPreview: setLivePreview,
        // Plain dictation yields one text; a diarized call segment yields one
        // statement per speaker turn, each with its `speaker: x` category.
        onStatement: async ({ text: transcript, statements }) => {
            const items =
                statements.length > 0
                    ? statements
                    : transcript
                    ? [{ text: transcript, categories: [] }]
                    : []

            if (!latestFormRef.current.autoSave) {
                appendPending(items)
                return
            }

            for (const [index, item] of items.entries()) {
                const form = latestFormRef.current
                // Anything already typed becomes part of the first statement.
                const typed = index === 0 ? form.text.trim() : ''
                const statement = [typed, item.text].filter(Boolean).join(' ')
                if (!statement) continue
                setText(statement)
                await form.onSave({
                    text: statement,
                    tags: [...new Set([...form.tags, ...(item.categories || [])])],
                    extractEntities: form.extractEntities,
                })
                // Clear only what was saved — keep anything typed meanwhile.
                setText(current =>
                    current.startsWith(statement)
                        ? current.slice(statement.length).trimStart()
                        : current
                )
            }
        },
        onError: err =>
            showSnackbar('error', err?.message || 'Voice input failed.'),
        onEnded: () =>
            showSnackbar('info', 'Sharing stopped — voice input is off.'),
    })

    const [sourceMenuAnchor, setSourceMenuAnchor] = useState(null)
    // A call source requested by a page load (the /apps Voice chip) can't
    // open the browser's share picker without a click: it waits on the mic
    // button, which then starts it directly instead of opening the menu.
    const [pendingCallSource, setPendingCallSource] = useState(null)

    const handleAutoSaveChange = checked => {
        setAutoSave(checked)
        // Switching back on saves what piled up while it was off.
        if (checked && voice.isListening && text.trim()) doSave()
    }
    const isCallMode = isCallSource(voice.sourceKind)

    const isVoiceAvailable = voiceEnabled && isVoiceDictationSupported()

    // While dictating, the field shows what is being said; typing resumes
    // when the mic is stopped, so typed and spoken words can't interleave.
    const displayedText = [text.trim(), livePreview].filter(Boolean).join(' ')
    const fieldValue = voice.isListening ? displayedText : text

    // Keep the newest words in view while listening: the field is read-only
    // then, so following the end of the text can't fight the user's caret.
    useEffect(() => {
        if (!voice.isListening) return
        const textarea = textareaRef.current
        if (textarea) textarea.scrollTop = textarea.scrollHeight
    }, [fieldValue, voice.isListening])

    // Closing the form (save, cancel, panel switch) releases the microphone.
    useEffect(() => {
        if (!open) voice.stop()
    }, [open, voice.stop])

    // Consume the "start dictating" intent (import dialog mic button) once
    // the form is open; a late-opening form still sees the flag.
    const voiceInputRequested = useZustand(state => state.voiceInputRequested)
    useEffect(() => {
        if (!voiceInputRequested || !open || !isVoiceAvailable) return
        useZustand.getState().clearVoiceInputRequest()
        const request =
            voiceInputRequested === true ? undefined : voiceInputRequested
        if (request && isCallSource(request.source)) {
            setPendingCallSource(request)
            showSnackbar(
                'info',
                'Click the microphone to start sharing the call audio.'
            )
            return
        }
        voice.start(request)
    }, [voiceInputRequested, open, isVoiceAvailable, voice.start])

    const handleMicClick = event => {
        if (voice.isListening) {
            voice.stop()
            return
        }
        if (pendingCallSource) {
            const request = pendingCallSource
            setPendingCallSource(null)
            voice.start(request)
            return
        }
        setSourceMenuAnchor(event.currentTarget)
    }

    const handleCancel = () => {
        voice.stop()
        onCancel()
    }
    // --- Voice dictation end ---

    // --- Autocomplete logic start ---
    const [suggestions, setSuggestions] = useState([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedSuggestion, setSelectedSuggestion] = useState(0)
    const [autocompleteRange, setAutocompleteRange] = useState(null) // {start, end}
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'

    // Helper: get caret position and current word/trigger
    function getCurrentWordData(value, caretPos) {
        // Find the word fragment before the caret
        const before = value.slice(0, caretPos)
        // Find last [[ or word boundary
        const match = before.match(/(\[\[|\b\w{2,})$/)
        if (!match) return null
        const trigger = match[0]
        const start = before.lastIndexOf(trigger)
        return { trigger, start, end: caretPos }
    }

    // Fetch suggestions onChange
    const handleChange = e => {
        const value = e.target.value
        setText(value)
        const caretPos = e.target.selectionStart
        const wordData = getCurrentWordData(value, caretPos)
        let allNodes = []
        if (graphFactory && graphFactory.getNodeNames) {
            allNodes = graphFactory.getNodeNames()
            // console.log('graphFactory.getNodeNames:', allNodes)
        }
        if (
            wordData &&
            (wordData.trigger.startsWith('[[') || wordData.trigger.length >= 2)
        ) {
            const fragment = wordData.trigger.startsWith('[[')
                ? ''
                : wordData.trigger
            const filtered = allNodes.filter(name =>
                name.toLowerCase().includes(fragment.toLowerCase())
            )
            setSuggestions(filtered.slice(0, 10))
            setShowSuggestions(filtered.length > 0)
            setSelectedSuggestion(0)
            setAutocompleteRange({ start: wordData.start, end: caretPos })
            setTimeout(updateDropdownPos, 0)
        } else {
            setShowSuggestions(false)
        }
    }

    const handleSelect = event => {
        const { value, selectionStart, selectionEnd } = event.target
        const newValue = wrapSelectionInBrackets(
            value,
            selectionStart,
            selectionEnd
        )
        if (newValue !== null) setText(newValue)
    }

    // Update dropdown position based on caret
    function updateDropdownPos() {
        if (!textareaRef.current || !mirrorRef.current || !autocompleteRange)
            return
        const textarea = textareaRef.current
        const mirror = mirrorRef.current
        // Copy scroll
        mirror.scrollTop = textarea.scrollTop
        // Get marker span
        const marker = mirror.querySelector('#caret-marker')
        if (marker) {
            const rect = marker.getBoundingClientRect()
            const parentRect = mirror.parentNode.getBoundingClientRect()
            setDropdownPos({
                top: rect.top - parentRect.top + 20, // 20px for line height
                left: rect.left - parentRect.left,
            })
        }
    }

    // Update dropdown position on text/selection change
    useEffect(() => {
        if (showSuggestions) {
            updateDropdownPos()
        }
        // eslint-disable-next-line
    }, [text, autocompleteRange, showSuggestions])

    // Handle key navigation
    const handleKeyDown = e => {
        if (showSuggestions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setSelectedSuggestion(i =>
                    Math.min(i + 1, suggestions.length - 1)
                )
                return
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setSelectedSuggestion(i => Math.max(i - 1, 0))
                return
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault()
                if (suggestions[selectedSuggestion]) {
                    insertSuggestion(suggestions[selectedSuggestion])
                }
                return
            } else if (e.key === 'Escape') {
                setShowSuggestions(false)
                return
            }
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSave()
        }
    }

    // Insert suggestion at cursor
    function insertSuggestion(suggestion) {
        console.log('inserting suggestion', suggestion)
        if (!autocompleteRange) return
        // If the fragment starts with [[, replace from the [[, not after it
        let start = autocompleteRange.start
        let before = text.slice(0, start)
        let after = text.slice(autocompleteRange.end)
        // Check if the preceding two chars are [[ and the fragment matches [[... (to handle [[assimi case)
        if (
            start >= 2 &&
            text.slice(start - 2, start) === '[[' &&
            text.slice(start - 2, autocompleteRange.end).startsWith('[[')
        ) {
            before = text.slice(0, start - 2)
        }
        const newText = before + suggestion + ' ' + after
        setText(newText)
        setShowSuggestions(false)
        setTimeout(() => {
            if (textareaRef.current) {
                const pos = before.length + suggestion.length + 1
                textareaRef.current.focus()
                textareaRef.current.setSelectionRange(pos, pos)
            }
        }, 0)
    }
    // --- Autocomplete logic end ---

    return (
        <Fade in={open} timeout={150} onExited={onEnd}>
            <div
                style={{
                    width: '100%',
                    padding: '8px 8px',
                    marginBottom: '4px',
                    borderRadius: '4px',
                    backgroundColor: theme => theme.palette.background.dialogs,
                    zIndex: 1,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'start',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ position: 'relative', width: '100%' }}>
                        <TextareaAutosize
                            ref={textareaRef} // Attach the ref to the textarea
                            minRows={5}
                            maxRows={TEXTAREA_MAX_ROWS}
                            placeholder={
                                voice.isListening && !autoSave
                                    ? 'listening… collecting what is said here; click Save to add it to the graph'
                                    : isCallMode
                                    ? 'listening to the call… every pause adds what was said'
                                    : voice.isListening
                                    ? 'listening… speak, then pause to add the statement'
                                    : 'type or paste your text here or import from external sources'
                            }
                            readOnly={voice.isListening}
                            aria-live={voice.isListening ? 'polite' : undefined}
                            style={{
                                width: '100%',
                                outline: 'none',
                                resize: 'none',
                                fontFamily: 'inherit',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                color: livePreview
                                    ? theme.palette.text.secondary
                                    : undefined,
                                backgroundColor: 'transparent',
                                padding: '0',
                                paddingBottom: isVoiceAvailable
                                    ? VOICE_CONTROLS_GUTTER
                                    : 0,
                                boxSizing: 'border-box',
                                margin: '0',
                                border: 0,
                                boxShadow: 'none',
                                borderRadius: '0',
                                position: 'relative',
                                zIndex: 2,
                            }}
                            value={fieldValue}
                            onChange={handleChange}
                            onSelect={handleSelect}
                            onKeyDown={handleKeyDown}
                        />
                        {/* Mirror div for caret-tracking */}
                        <div
                            ref={mirrorRef}
                            aria-hidden="true"
                            style={{
                                position: 'absolute',
                                pointerEvents: 'none',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                visibility: 'hidden',
                                overflow: 'hidden',
                                width: '100%',
                                minHeight: 80,
                                fontFamily: 'inherit',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                padding: '0',
                                paddingBottom: isVoiceAvailable
                                    ? VOICE_CONTROLS_GUTTER
                                    : 0,
                                margin: '0',
                                border: 0,
                                boxSizing: 'border-box',
                                background: 'transparent',
                                zIndex: 1,
                            }}
                            // dangerouslySetInnerHTML below
                        >
                            {(() => {
                                // Render text up to caret, then marker span, then rest
                                if (!autocompleteRange) return text
                                const before = text.slice(
                                    0,
                                    autocompleteRange.end
                                )
                                const after = text.slice(autocompleteRange.end)
                                // Replace spaces and newlines for HTML rendering
                                const safeBefore = before
                                    .replace(/\n/g, '<br/>')
                                    .replace(/ /g, '&nbsp;')
                                const safeAfter = after
                                    .replace(/\n/g, '<br/>')
                                    .replace(/ /g, '&nbsp;')
                                return (
                                    (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: safeBefore,
                                            }}
                                        />
                                    ),
                                    (
                                        <span
                                            id="caret-marker"
                                            style={{
                                                display: 'inline-block',
                                                width: 1,
                                                height: '1em',
                                                background: 'transparent',
                                            }}
                                        ></span>
                                    ),
                                    (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: safeAfter,
                                            }}
                                        />
                                    )
                                )
                            })()}
                        </div>
                        {isVoiceAvailable && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    zIndex: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                {voice.isListening && (
                                <Tooltip
                                    title={
                                        autoSave
                                            ? 'Auto-save is on: every pause adds a statement to the graph'
                                            : 'Auto-save is off: what is said collects here until you click Save'
                                    }
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={autoSave}
                                                onChange={event =>
                                                    handleAutoSaveChange(
                                                        event.target.checked
                                                    )
                                                }
                                                inputProps={{
                                                    'aria-label':
                                                        'Auto-save statements',
                                                    'data-testid':
                                                        'voice-autosave',
                                                }}
                                            />
                                        }
                                        label="auto-save"
                                        sx={{
                                            mr: 0.5,
                                            ml: 0,
                                            '& .MuiTypography-root': {
                                                fontSize: 11,
                                                color: 'text.secondary',
                                            },
                                        }}
                                    />
                                </Tooltip>
                                )}
                                {voice.isTranscribing && (
                                    <Tooltip title="Transcribing…">
                                        <CircularProgress
                                            size={14}
                                            aria-label="Transcribing"
                                        />
                                    </Tooltip>
                                )}
                                {isCallMode && (
                                    <Tooltip
                                        title={`Listening to the ${
                                            voice.sourceKind === 'screen'
                                                ? "computer's audio"
                                                : 'shared tab'
                                        }${
                                            voice.includesMic
                                                ? ' and your microphone'
                                                : ''
                                        }${
                                            voice.hasSpeakerReference
                                                ? '; your voice is tagged speaker: me'
                                                : ''
                                        }`}
                                    >
                                        <Box
                                            data-testid="voice-live-badge"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                color: 'error.main',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                '@keyframes voiceLive': {
                                                    '0%, 100%': { opacity: 1 },
                                                    '50%': { opacity: 0.3 },
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor:
                                                        'error.main',
                                                    animation:
                                                        'voiceLive 1.2s ease-in-out infinite',
                                                }}
                                            />
                                            LIVE
                                        </Box>
                                    </Tooltip>
                                )}
                                <Tooltip
                                    title={
                                        voice.isListening
                                            ? 'Stop voice input'
                                            : pendingCallSource
                                            ? 'Start sharing the call audio'
                                            : 'Voice input: dictate, or transcribe a call or video'
                                    }
                                >
                                    <IconButton
                                        size="small"
                                        aria-label={
                                            voice.isListening
                                                ? 'Stop voice input'
                                                : 'Start voice input'
                                        }
                                        aria-pressed={voice.isListening}
                                        aria-haspopup={
                                            voice.isListening ||
                                            pendingCallSource
                                                ? undefined
                                                : 'menu'
                                        }
                                        onClick={handleMicClick}
                                        sx={{
                                            color: voice.isListening
                                                ? 'error.main'
                                                : pendingCallSource
                                                ? 'warning.main'
                                                : '#6688ff',
                                            '@keyframes voicePulse': {
                                                '0%, 100%': { opacity: 1 },
                                                '50%': { opacity: 0.35 },
                                            },
                                            animation:
                                                voice.isSpeaking ||
                                                pendingCallSource
                                                    ? 'voicePulse 0.8s ease-in-out infinite'
                                                    : 'none',
                                        }}
                                    >
                                        {voice.isListening ? (
                                            <MicIcon fontSize="small" />
                                        ) : (
                                            <MicNoneIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                                <VoiceSourceMenu
                                    anchorEl={sourceMenuAnchor}
                                    open={!!sourceMenuAnchor}
                                    onClose={() => setSourceMenuAnchor(null)}
                                    onSelect={selection =>
                                        voice.start(selection)
                                    }
                                />
                            </Box>
                        )}
                        {showSuggestions && suggestions.length > 0 && (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: dropdownPos.left,
                                    top: dropdownPos.top,
                                    background: isDark
                                        ? theme.palette.background.paper
                                        : '#fff',
                                    color: isDark ? '#fff' : '#222',
                                    border: `1px solid ${
                                        isDark ? theme.palette.divider : '#ccc'
                                    }`,
                                    borderRadius: 4,
                                    zIndex: 20,
                                    minWidth: 180,
                                    maxWidth: 340,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    boxShadow: isDark
                                        ? '0 2px 8px rgba(0,0,0,0.55)'
                                        : '0 2px 8px rgba(0,0,0,0.12)',
                                }}
                            >
                                {suggestions.map((s, i) => (
                                    <div
                                        key={s}
                                        style={{
                                            padding: '6px 12px',
                                            background:
                                                i === selectedSuggestion
                                                    ? isDark
                                                        ? theme.palette.action
                                                              .selected
                                                        : '#f0f6ff'
                                                    : 'transparent',
                                            color: isDark ? '#fff' : '#222',
                                            cursor: 'pointer',
                                            fontWeight:
                                                i === selectedSuggestion
                                                    ? 600
                                                    : 400,
                                        }}
                                        onMouseDown={e => {
                                            e.preventDefault()
                                            insertSuggestion(s)
                                        }}
                                        onMouseEnter={() =>
                                            setSelectedSuggestion(i)
                                        }
                                    >
                                        {s}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <Tags selectedTags={tags} onTagsChange={setTags} />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={extractEntities}
                                size="small"
                                onChange={e =>
                                    handleExtractEntitiesChange(
                                        e.target.checked
                                    )
                                }
                                inputProps={{
                                    'aria-label': 'extract entities',
                                }}
                            />
                        }
                        sx={{
                            '& .MuiTypography-root': { fontSize: 14 },
                        }}
                        label="extract entities"
                    />
                    {comparisonMode && (
                        <Tooltip
                            title={`The statement will be added to the following context`}
                        >
                            <Chip
                                label={label}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{
                                    height: '20px',
                                    fontSize: '12px',
                                    cursor: 'default',
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
                <div
                    style={{
                        marginTop: '10px',
                        textAlign: 'right',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '8px',
                        color: 'black',
                    }}
                >
                    <Button onClick={handleCancel}>Cancel</Button>

                    <Button
                        color="info"
                        size="small"
                        fontSize="small"
                        variant="outlined"
                        endIcon={<SaveIcon />}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </div>
                <Dialog
                    open={showEntityWarning}
                    onClose={() => setShowEntityWarning(false)}
                >
                    <DialogTitle>Manual Tags Detected</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Automatic entity detection is on, but you tagged
                            entities manually in your statement. Do you still
                            want to proceed or cancel to toggle the entity
                            detection off first?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowEntityWarning(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setShowEntityWarning(false)
                                handleExtractEntitiesChange(false)
                                doSave()
                            }}
                        >
                            Turn Off & Proceed
                        </Button>
                        <Button
                            onClick={() => {
                                setShowEntityWarning(false)
                                doSave()
                            }}
                            variant="contained"
                        >
                            Proceed Anyway
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </Fade>
    )
}

export default StatementTextarea
